import React from 'react'
import StatusBadge from '../StatusBadge'
import Field from '../../common/FormField'
import {
  statusCodes,
  statusCodeToText,
  isSoldStatusCode
} from '../../../../common/utils/diamond'

import styles from '../Form.module.css'

export default ({
  data,
  loadedStatusCode,
  onChangeFactory,
  disabled,
  isAdmin
}) => {
  const handleStatusCodeChange = onChangeFactory('statusCode')
  // Keeps the local `status` text field (also submitted to the API) in step
  // with `statusCode`. Previously only statusCode was updated here, so
  // switching e.g. Available -> Reserved left the stale `status` text from
  // whenever the form loaded — the two fields drifted apart on save.
  const handleStatusTextChange = onChangeFactory('status')
  const handleChange = event => {
    const value = parseInt(event.target.value)
    handleStatusCodeChange({
      target: {
        type: 'radio',
        value
      }
    })
    handleStatusTextChange({
      target: {
        type: 'radio',
        value: statusCodeToText(value)
      }
    })
  }
  // Which radio is filled in follows the live selection; which radios are open
  // follows the item as it was loaded. Gating on the live value made the group
  // rearrange itself under the cursor — moving a sold item to Hidden re-opened
  // Available and closed Sold, so there was no way back to where you started.
  const isSold = isSoldStatusCode(data.statusCode)
  const wasSold = isSoldStatusCode(loadedStatusCode)
  const wasDeleted = loadedStatusCode === statusCodes.DELETED.value
  const canChangeStatus = isAdmin || (!wasSold && !wasDeleted && !disabled)
  // `sold` is the flag the API raises when an item is actually bought out, and
  // it outlives a status edit — the update endpoint leaves it alone whenever the
  // request carries it, which this form always does. So it still reads true
  // after the item has been moved to Reserved or Hidden, which is what keeps
  // this answer steady once the status code itself no longer says "sold".
  const isMarkedSold = !!data.sold || wasSold
  // Nothing left to sell. Recurring items are excluded because they are never
  // depleted, matching Inventory::syncStockStatus on the API side.
  const hasNoStock = !data.recurring && !(parseInt(data.quantity, 10) > 0)
  // Read the same way the API reads it — a missing quantity counts as none —
  // so the figure shown next to Sold cannot disagree with the gate above.
  const stockQuantity = parseInt(data.quantity, 10) || 0
  // Available stays closed on a sold item even for an admin: it has nothing
  // left to sell, and listing it only puts it in front of buyers the purchase
  // endpoint then turns away as out of stock.
  //
  // Keyed to `sold` and not to stock on purpose. quantity defaults to 0 for
  // every item created through this form — store() sets it and never re-derives
  // the status from it — so a gate on stock alone would close Available on
  // ordinary new inventory that is perfectly listable.
  const canSetAvailable = canChangeStatus && !isMarkedSold
  // Sold is pickable on anything with no stock left, and on an item already
  // marked sold even if it still has some: both need a way back to Sold after a
  // detour through Reserved or Hidden. It stays closed on a stocked item that
  // was never sold — a sale is what marks an item sold, not this form.
  const canSetSold = canChangeStatus && (isMarkedSold || hasNoStock)
  // Re-picking Sold writes back the code the item already carried, or else the
  // one the API itself writes for a depleted row — never a rewrite of 5 to 4.
  const soldValue = wasSold ? loadedStatusCode : statusCodes.SOLD_OUT.value
  return (
    <fieldset>
      <Field
        type="radio"
        note={
          isMarkedSold
            ? 'This item is sold. It can be listed as Available again once it is back in stock.'
            : 'Keep the status Available to allow users to search for this item.'
        }
        radioDisabled={!canSetAvailable}
      >
        <input
          type="radio"
          name="statusGroup"
          value={statusCodes.AVAILABLE.value}
          checked={data.statusCode === statusCodes.AVAILABLE.value}
          onChange={handleChange}
          disabled={!canSetAvailable}
        />
        <i />
        <StatusBadge className={styles.statusBadge} status="available" />
      </Field>
      <Field
        type="radio"
        note="Change the status of the item to Reserved if you are holding it. Users can still see this item if saved, but not search for it."
      >
        <input
          type="radio"
          name="statusGroup"
          value={statusCodes.RESERVED.value}
          checked={data.statusCode === statusCodes.RESERVED.value}
          onChange={handleChange}
          disabled={!canChangeStatus}
        />
        <i />
        <StatusBadge className={styles.statusBadge} status="reserved" />
      </Field>
      <Field
        type="radio"
        note="Only hide item if you are not ready to list it, or if there is an inventory issue. Users can not see this item in any form, and can not search for it."
      >
        <input
          type="radio"
          name="statusGroup"
          value={statusCodes.HIDDEN.value}
          checked={data.statusCode === statusCodes.HIDDEN.value}
          onChange={handleChange}
          disabled={!canChangeStatus}
        />
        <i />
        <StatusBadge className={styles.statusBadge} status="hidden" />
      </Field>
      <Field
        type="radio"
        note="If a sale is made via Gemsby, the item will be marked as Sold automatically. Users can still see this item if saved, but not search for it."
        radioDisabled={!canSetSold}
      >
        <input
          type="radio"
          name="statusGroup"
          value={soldValue}
          checked={isSold}
          onChange={handleChange}
          disabled={!canSetSold}
        />
        <i />
        <StatusBadge className={styles.statusBadge} status="sold" />
        <span className={styles.stockCount}>
          {data.recurring
            ? 'Recurring item — stock is never depleted'
            : `Stock quantity: ${stockQuantity}`}
        </span>
      </Field>
      <Field type="radio" note="This item is deleted." radioDisabled={true}>
        <input
          type="radio"
          name="statusGroup"
          value={statusCodes.DELETED.value}
          checked={data.statusCode === statusCodes.DELETED.value}
          onChange={handleChange}
          disabled
        />
        <i />
        <StatusBadge className={styles.statusBadge} status="deleted" />
      </Field>
    </fieldset>
  )
}
